import { useState, useMemo, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useDragScroll, DragScrollContainer } from "@/hooks/useDragScroll";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDiet, type SavedDiet, getAllRecipeIds } from "@/contexts/DietContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  Coffee,
  Sun,
  Cookie,
  Moon,
  Plus,
  UtensilsCrossed,
  Flame,
  Beef,
  Wheat,
  Droplets,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Info,
  ArrowRightLeft,
  Calendar,
  Target,
  History,
  Star,
  Trash2,
  FileDown,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import {
  recipes,
  mealOrder,
  findRecipeById,
  parseScaledRecipeId,
  buildScaledRecipeId,
  type MealKey,
  type Recipe,
} from "@/data/recipes";
import { useSavedRecipes, getRecipeTotals, type SavedRecipe } from "@/hooks/useSavedRecipes";
import { calcMacroTargets } from "@/lib/macroSolver";
import { cn } from "@/lib/utils";
import {
  getNormalizedMealBudgets,
  rebalanceDietAcrossMeals,
  closeMealOptionToBudget,
} from "@/lib/dietOptionRebalance";
import { useDietRecipes } from "@/hooks/useDietRecipes";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import MyDietSkeleton from "@/components/diet/MyDietSkeleton";
import RecipeEmoji from "@/components/RecipeEmoji";

const mealConfig: Record<string, { label: string; icon: React.ElementType; emoji: string }> = {
  breakfast: { label: "Café da Manhã", icon: Coffee, emoji: "🌅" },
  morning_snack: { label: "Lanche da Manhã", icon: Sun, emoji: "🍎" },
  lunch: { label: "Almoço", icon: Sun, emoji: "🍽️" },
  snack: { label: "Lanche da Tarde", icon: Cookie, emoji: "🍪" },
  dinner: { label: "Jantar", icon: Moon, emoji: "🌙" },
  optional: { label: "Lanches Opcionais", icon: Plus, emoji: "🍿" },
};

const MyDiet = () => {
  const {
    dietPlan,
    meals,
    setMeals,
    saveDiet,
    savedDiets,
    loadDiet,
    setCurrentDiet,
    deleteDiet,
    currentDietId,
    historyLoading,
    saving,
    selectedPlan,
  } = useDiet();
  const { recipes: userRecipes, globalRecipes } = useSavedRecipes();
  const { enrichedRecipes } = useDietRecipes();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [expandedMeal, setExpandedMeal] = useState<string | null>(null);
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);
  const [swappingMeal, setSwappingMeal] = useState<string | null>(null);
  const [swappingRecipeId, setSwappingRecipeId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeOption, setActiveOption] = useState<Record<string, number>>({});
  const [deleteTarget, setDeleteTarget] = useState<{
    mealKey: string;
    recipeId: string;
    optIdx: number;
    label: string;
  } | null>(null);
  const [addItemTarget, setAddItemTarget] = useState<{ mealKey: string; optIdx: number } | null>(null);
  const [pendingExceedItem, setPendingExceedItem] = useState<{
    mealKey: string;
    optIdx: number;
    candidate: any;
  } | null>(null);
  const globalOptionsDrag = useDragScroll();

  const allSavedRecipes = useMemo(() => [...userRecipes, ...globalRecipes], [userRecipes, globalRecipes]);

  /** Convert a SavedRecipe to the Recipe format used by MyDiet */
  const savedToRecipe = useCallback((saved: SavedRecipe): Recipe => {
    const totals = getRecipeTotals(saved);
    return {
      id: `saved_${saved.id}`,
      label: saved.name,
      emoji: "📋",
      cal: Math.round(totals.kcal),
      protein: Math.round(totals.protein),
      ingredients: saved.recipe_ingredients.map((ing) => {
        const factor = ing.quantity_grams / 100;
        const food = ing.taco_foods;
        return {
          name: food.description,
          amount: `${(Math.round(ing.quantity_grams * 10) / 10).toString().replace(/\.0$/, "")}g`,
          cal: Math.round((food.energy_kcal || 0) * factor),
          protein: Math.round((food.protein_g || 0) * factor * 10) / 10,
          carbs: Math.round((food.carbohydrate_g || 0) * factor * 10) / 10,
          fat: Math.round((food.lipid_g || 0) * factor * 10) / 10,
        };
      }),
    };
  }, []);

  /** Resolve a recipe ID (hardcoded or saved_*) to a Recipe */
  const resolveRecipe = useCallback(
    (id: string): Recipe | undefined => {
      if (id.startsWith("saved_")) {
        const { baseId, scale } = parseScaledRecipeId(id);
        const savedId = baseId.replace("saved_", "");
        const saved = allSavedRecipes.find((r) => r.id === savedId);
        if (!saved) return undefined;

        const baseRecipe = savedToRecipe(saved);
        if (Math.abs(scale - 1) < 0.0001) return baseRecipe;

        const scaledIngredients = baseRecipe.ingredients.map((ing) => ({
          ...ing,
          amount: ing.amount
            .replace(
              /(\d+(?:\.\d+)?)\s*g/gi,
              (_, n) => `${(Math.round(Number(n) * scale * 10) / 10).toString().replace(/\.0$/, "")}g`,
            )
            .replace(
              /(\d+(?:\.\d+)?)\s*ml/gi,
              (_, n) => `${(Math.round(Number(n) * scale * 10) / 10).toString().replace(/\.0$/, "")}ml`,
            ),
          cal: Math.round(ing.cal * scale),
          protein: Math.round(ing.protein * scale * 10) / 10,
          carbs: Math.round(ing.carbs * scale * 10) / 10,
          fat: Math.round(ing.fat * scale * 10) / 10,
        }));

        return {
          ...baseRecipe,
          id,
          cal: scaledIngredients.reduce((sum, i) => sum + i.cal, 0),
          protein: Math.round(scaledIngredients.reduce((sum, i) => sum + i.protein, 0) * 10) / 10,
          ingredients: scaledIngredients,
        };
      }

      return findRecipeById(id, enrichedRecipes);
    },
    [allSavedRecipes, savedToRecipe, enrichedRecipes],
  );

  const getValidOptionIndices = (mealKey: string): number[] => {
    if (!dietPlan) return [0];
    const options = dietPlan.meals[mealKey as MealKey] || [];
    return options.map((opt, idx) => (opt.length > 0 ? idx : -1)).filter((idx) => idx >= 0);
  };

  const getActiveOptionIndex = (mealKey: string) => {
    const validIndices = getValidOptionIndices(mealKey);
    const preferred = activeOption[mealKey];
    if (typeof preferred === "number" && validIndices.includes(preferred)) return preferred;
    return validIndices[0] ?? 0;
  };

  const getOptionRecipeEntries = (mealKey: string, optIdx: number): { id: string; recipe: Recipe }[] => {
    if (!dietPlan) return [];
    const options = dietPlan.meals[mealKey as MealKey] || [[]];
    const ids = options[optIdx] || [];

    return ids
      .map((id) => {
        const recipe = resolveRecipe(id);
        return recipe ? { id, recipe } : null;
      })
      .filter((entry): entry is { id: string; recipe: Recipe } => Boolean(entry));
  };

  const getOptionRecipes = (mealKey: string, optIdx: number): Recipe[] => {
    return getOptionRecipeEntries(mealKey, optIdx).map(({ recipe }) => recipe);
  };

  /**
   * Compute meal totals consistent with the diet generator:
   * - Salads ("à vontade") are excluded
   * - Items tagged with `:free` (low-cal beverages) are excluded
   * This ensures the UI total matches the calorie target the solver optimized against.
   */
  const getMealTotalsFromEntries = (entries: { id: string; recipe: Recipe }[]) => {
    return entries
      .filter(({ id, recipe }) => recipe.category !== "salad" && !id.endsWith(":free"))
      .reduce(
        (acc, { recipe: r }) => {
          const carbs = r.ingredients.reduce((s, i) => s + i.carbs, 0);
          const fat = r.ingredients.reduce((s, i) => s + i.fat, 0);
          return {
            cal: acc.cal + r.cal,
            protein: acc.protein + r.protein,
            carbs: acc.carbs + carbs,
            fat: acc.fat + fat,
          };
        },
        { cal: 0, protein: 0, carbs: 0, fat: 0 },
      );
  };

  /** Legacy helper kept for callers that only have Recipe[] (no IDs). Excludes salads only. */
  const getMealTotals = (mealRecipes: Recipe[]) => {
    return mealRecipes
      .filter((r) => r.category !== "salad")
      .reduce(
        (acc, r) => {
          const carbs = r.ingredients.reduce((s, i) => s + i.carbs, 0);
          const fat = r.ingredients.reduce((s, i) => s + i.fat, 0);
          return {
            cal: acc.cal + r.cal,
            protein: acc.protein + r.protein,
            carbs: acc.carbs + carbs,
            fat: acc.fat + fat,
          };
        },
        { cal: 0, protein: 0, carbs: 0, fat: 0 },
      );
  };

  /** Build entries (id+recipe) for a meal option. */
  const getOptionEntries = (mealKey: string, optIdx: number) => getOptionRecipeEntries(mealKey, optIdx);

  const globalTotals = useMemo(() => {
    if (!dietPlan) return { cal: 0, protein: 0, carbs: 0, fat: 0 };
    let cal = 0,
      protein = 0,
      carbs = 0,
      fat = 0;

    const activeMealsForTotals = mealOrder.filter((key) => {
      const options = dietPlan.meals[key] || [];
      return options.some((opt) => opt.length > 0);
    });

    for (const key of activeMealsForTotals) {
      const totals = getMealTotalsFromEntries(getOptionEntries(key, getActiveOptionIndex(key)));
      cal += totals.cal;
      protein += totals.protein;
      carbs += totals.carbs;
      fat += totals.fat;
    }

    return {
      cal: Math.round(cal),
      protein: Math.round(protein * 10) / 10,
      carbs: Math.round(carbs * 10) / 10,
      fat: Math.round(fat * 10) / 10,
    };
  }, [dietPlan, activeOption, resolveRecipe]);

  const activeMealKeys = useMemo(() => {
    if (!dietPlan) return [] as MealKey[];

    return mealOrder.filter((key) => {
      const options = dietPlan.meals[key] || [];
      return options.some((opt) => opt.length > 0);
    });
  }, [dietPlan]);

  const budgets = useMemo(() => {
    if (!dietPlan) return getNormalizedMealBudgets(2000, mealOrder);
    return getNormalizedMealBudgets(dietPlan.measures.calories, activeMealKeys);
  }, [dietPlan, activeMealKeys]);

  // Fix legacy diets that saved macroTargets with 0g carbs
  const correctedMacroTargets = useMemo(() => {
    if (!dietPlan) return null;
    const mt = dietPlan.measures.macroTargets;
    if (!mt) return null;
    if (mt.carbs > 0) return mt;
    // Recalculate using the fixed formula
    const { weight, goal, calories } = dietPlan.measures;
    if (weight && goal && calories) {
      return calcMacroTargets(weight, goal, calories);
    }
    return mt;
  }, [dietPlan]);

  // PDF Export — reusable for any DietPlan
  const exportPDFForPlan = useCallback(
    async (plan: { measures: any; meals: any; createdAt: string; name?: string }) => {
      const { generateDietPdfBlob } = await import("@/components/diet/DietPdfDocument");

      const activeMeals = mealOrder.filter((key) => {
        const options = plan.meals[key as MealKey];
        return options && options.some((opt: string[]) => opt.length > 0);
      });

      const measures = plan.measures;
      const heightM = measures.height > 3 ? measures.height / 100 : measures.height;
      const imc = measures.weight && heightM ? (measures.weight / (heightM * heightM)).toFixed(1) : "--";
      const waterMultiplier = measures.goal === "gain" ? 0.045 : measures.goal === "lose" ? 0.04 : 0.035;
      const waterLiters = measures.weight
        ? Math.min(4, Math.max(2, measures.weight * waterMultiplier)).toFixed(2)
        : "--";

      const mealTimes = measures.mealTimes || [];
      const mealTimeMap: Record<string, string> = {
        breakfast: mealTimes[0] || "07:00",
        morning_snack: mealTimes[1] || "10:00",
        lunch: mealTimes[2] || "12:00",
        snack: mealTimes[3] || "15:00",
        dinner: mealTimes[4] || "19:00",
        optional: mealTimes[5] || "",
      };

      const pdfMeals = activeMeals.map((key) => {
        const config = mealConfig[key];
        const options = plan.meals[key as MealKey] || [[]];
        const validOptions = (options as string[][]).filter((opt) => opt.length > 0);

        const optionCalories = validOptions.map((opt) => {
          const entries = opt
            .map((id) => {
              const recipe = resolveRecipe(id);
              return recipe ? { id, recipe } : null;
            })
            .filter((e): e is { id: string; recipe: Recipe } => Boolean(e));
          return getMealTotalsFromEntries(entries).cal;
        });
        const avgCal =
          optionCalories.length > 0 ? Math.round(optionCalories.reduce((a, b) => a + b, 0) / optionCalories.length) : 0;

        return {
          key,
          label: config.label,
          time: mealTimeMap[key],
          avgCal,
          options: validOptions.map((opt, optIdx) => {
            const mealRecipes = opt.map((id) => resolveRecipe(id)).filter(Boolean) as Recipe[];
            return {
              cal: Math.round(optionCalories[optIdx] || 0),
              recipes: mealRecipes.map((r) => ({
                label: r.label,
                category: r.category,
                ingredients: r.ingredients.map((ing) => ({
                  name: ing.name,
                  amount: ing.amount,
                })),
              })),
            };
          }),
        };
      });

      const blob = await generateDietPdfBlob({
        userName: user?.name,
        weight: measures.weight,
        height: heightM,
        imc,
        waterLiters,
        goal: measures.goal,
        calories: measures.calories,
        macroTargets:
          measures.macroTargets?.carbs === 0 && measures.weight && measures.goal && measures.calories
            ? calcMacroTargets(measures.weight, measures.goal, measures.calories)
            : measures.macroTargets,
        meals: pdfMeals,
        createdAt: new Date(plan.createdAt).toLocaleDateString("pt-BR"),
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (plan.name || `Dieta_${new Date().toISOString().slice(0, 10)}`)
        .replace(/[^a-zA-Z0-9À-ÿ _-]/g, "")
        .replace(/\s+/g, "_");
      a.download = `${safeName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [resolveRecipe],
  );

  const exportPDF = async () => {
    if (!dietPlan) return;
    setExporting(true);
    try {
      await exportPDFForPlan(dietPlan);
    } catch (e) {
      console.error("PDF export error:", e);
      toast({ title: "Erro ao exportar PDF", variant: "destructive" });
    }
    setExporting(false);
  };

  if ((historyLoading && !dietPlan) || saving) {
    return <MyDietSkeleton />;
  }

  if (!dietPlan) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <ClipboardList className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Nenhuma dieta encontrada</h2>
          <p className="text-muted-foreground mb-6">Monte sua dieta personalizada agora</p>
          <Button onClick={() => navigate("/dashboard")} className="gradient-primary text-primary-foreground">
            <UtensilsCrossed className="w-4 h-4 mr-2" />
            Montar minha dieta
          </Button>
        </div>

        {savedDiets.length > 0 && (
          <DietHistory
            diets={savedDiets}
            currentDietId={currentDietId}
            onExportPdf={async (diet) => {
              try {
                await exportPDFForPlan({
                  measures: diet.measures,
                  meals: diet.meals,
                  createdAt: diet.created_at,
                  name: diet.name,
                });
              } catch (e) {
                console.error("PDF export error:", e);
                toast({ title: "Erro ao exportar PDF", variant: "destructive" });
              }
            }}
            onDelete={deleteDiet}
            onSetCurrent={setCurrentDiet}
            loading={historyLoading}
            resolveRecipe={resolveRecipe}
          />
        )}
      </motion.div>
    );
  }

  const targetCalories = dietPlan.measures.calories;
  const caloriePercentRaw = Math.round((globalTotals.cal / targetCalories) * 100);
  const caloriePercent = Math.min(caloriePercentRaw, 100);

  const canSubstitute = selectedPlan === "plus" || selectedPlan === "complete";

  /** Returns substitution candidates scaled to match the current recipe's calories */
  const getSubstitutions = (mealKey: string, currentRecipeId: string, optIdx: number): Recipe[] => {
    if (!canSubstitute) return [];

    const block = enrichedRecipes[mealKey as MealKey];
    if (!block) return [];

    const options = dietPlan.meals[mealKey as MealKey] || [[]];
    const selectedIds = options[optIdx] || [];

    // Resolve current recipe (already scaled) to get its actual displayed calories
    const currentRecipe = resolveRecipe(currentRecipeId);
    const targetCal = currentRecipe?.cal || 0;
    if (targetCal <= 0) return [];

    // Get the category for matching
    const currentCategory = currentRecipe?.category;

    // Compute a fallback "role signature" for recipes without explicit category.
    // This prevents random/incoherent substitutions (e.g. swapping "Pão+Ovo" for
    // a salad or a fruit) when category metadata is missing.
    const computeRole = (r: Recipe): "protein" | "carb" | "fat" | "mixed" => {
      const totalCarbs = r.ingredients.reduce((s, i) => s + i.carbs, 0);
      const totalFat = r.ingredients.reduce((s, i) => s + i.fat, 0);
      const protCal = r.protein * 4;
      const carbCal = totalCarbs * 4;
      const fatCal = totalFat * 9;
      const total = protCal + carbCal + fatCal;
      if (total <= 0) return "mixed";
      const pP = protCal / total;
      const pC = carbCal / total;
      const pF = fatCal / total;
      if (pP >= 0.30 && pP >= pC && pP >= pF) return "protein";
      if (pC >= 0.50) return "carb";
      if (pF >= 0.50) return "fat";
      return "mixed";
    };
    const currentRole = currentRecipe ? computeRole(currentRecipe) : "mixed";

    // Get base IDs already in this option to avoid duplicates
    const selectedBaseIds = selectedIds.map((sid) => parseScaledRecipeId(sid).baseId);
    const currentBaseId = parseScaledRecipeId(currentRecipeId).baseId;

    // The current item is a salad/beverage? Only substitute with salad/beverage
    const currentIsSalad = currentRecipe?.category === "salad";
    const currentIsBeverage = currentRecipeId.endsWith(":free");

    // Bebida "free" nunca tem botão de troca (regra de produto)
    if (currentIsBeverage) return [];

    // Pré-seleciona candidatos válidos (regras invioláveis) e calcula sua escala
    type Cand = { recipe: (typeof block.items)[number]; scale: number; role: ReturnType<typeof computeRole> };
    const baseCandidates: Cand[] = [];
    for (const candidate of block.items) {
      if (candidate.id === currentBaseId || selectedBaseIds.includes(candidate.id)) continue;
      if (candidate.cal <= 0) continue;
      const candIsSalad = candidate.category === "salad";
      // Salada só troca com salada
      if (currentIsSalad !== candIsSalad) continue;
      const scale = targetCal / candidate.cal;
      // Janela de escala mais generosa (antes era 0.5x–2.0x)
      if (scale < 0.33 || scale > 3.0) continue;
      baseCandidates.push({ recipe: candidate, scale, role: computeRole(candidate) });
    }

    // Filtragem em camadas: tenta exato → role → qualquer não-salada
    const tier1 = baseCandidates.filter(({ recipe: c }) =>
      currentCategory && c.category ? c.category === currentCategory : false,
    );
    const tier2 = baseCandidates.filter(({ role }) => role === currentRole);
    const tier3 = baseCandidates;

    let chosen: Cand[] = tier1.length > 0 ? tier1 : tier2.length > 0 ? tier2 : tier3;
    // Deduplica por baseId preservando ordem
    const seen = new Set<string>();
    chosen = chosen.filter(({ recipe: c }) => (seen.has(c.id) ? false : (seen.add(c.id), true)));

    return chosen.map(({ recipe: candidate, scale }) => {
      const scaledIngredients = candidate.ingredients.map((ing) => ({
        ...ing,
        cal: Math.round(ing.cal * scale),
        protein: Math.round(ing.protein * scale * 10) / 10,
        carbs: Math.round(ing.carbs * scale * 10) / 10,
        fat: Math.round(ing.fat * scale * 10) / 10,
      }));
      return {
        ...candidate,
        id: buildScaledRecipeId(candidate.id, scale),
        cal: Math.round(targetCal),
        protein: Math.round(scaledIngredients.reduce((s, i) => s + i.protein, 0) * 10) / 10,
        ingredients: scaledIngredients,
      };
    });
  };

  const handleDeleteItem = (mealKey: string, recipeId: string, optIdx: number) => {
    const typedMealKey = mealKey as MealKey;
    const currentMeals = { ...dietPlan.meals };
    const options = [...currentMeals[typedMealKey].map((o) => [...o])];
    const option = options[optIdx] || [];

    // Remove the item
    const newOption = option.filter((id) => id !== recipeId);

    if (newOption.length === 0) {
      options[optIdx] = newOption;
      currentMeals[typedMealKey] = options;
      setMeals(currentMeals);
      saveDiet(undefined, currentMeals);
      toast({ title: "Item removido", description: "A refeição ficou vazia." });
      setDeleteTarget(null);
      return;
    }

    const fallbackEntries = option
      .map((id) => {
        const recipe = resolveRecipe(id);
        return recipe ? { id, recipe } : null;
      })
      .filter((e): e is { id: string; recipe: Recipe } => Boolean(e));
    const fallbackTarget = getMealTotalsFromEntries(fallbackEntries).cal;
    const targetCalories = budgets[typedMealKey] ?? fallbackTarget;

    // Build the candidate pool from items the user already accepted in this meal
    // across ALL options, plus the items currently visible in this option. This
    // keeps additions safe (already-validated preferences) while letting the
    // closer pull a compatible item to refill the budget when the remaining
    // ingredients can't structurally reach it.
    // Exclude the just-deleted item's baseId so the closer doesn't pull it
    // back into the same option to fill the calorie gap.
    const deletedBaseId = parseScaledRecipeId(recipeId.replace(/:free$/, "")).baseId;
    const preferenceIds = Array.from(
      new Set(
        (currentMeals[typedMealKey] || [])
          .flat()
          .map((id) => parseScaledRecipeId(id.replace(/:free$/, "")).baseId)
          .filter((baseId) => baseId !== deletedBaseId),
      ),
    );

    // Step 1: exact local closure of the modified meal.
    // maxAdditions: 0 — when the user deletes an item, respect that intent and
    // only rescale the remaining items. Don't auto-add a replacement (which
    // could re-pick the deleted item or another unwanted one).
    const closedOption = closeMealOptionToBudget({
      optionIds: newOption,
      targetCalories,
      resolveRecipe,
      availablePreferences: preferenceIds,
      maxAdditions: 0,
      maxOverRatio: 1.05,
      acceptRatio: 0.99,
    });

    options[optIdx] = closedOption;
    currentMeals[typedMealKey] = options;

    // Step 2: ALWAYS lock the modified meal after closure. The closer already
    // picked the closest-to-budget option (or best in-band). Letting the daily
    // solver re-open it causes deficits when structural floors prevent exact
    // budget matches (e.g. a single kiwi at 36 kcal vs. 77 kcal target when
    // 2 kiwis = 72 kcal is structurally closer). The daily total residue should
    // be absorbed by the OTHER meals instead of collapsing this one back to a
    // worse fit.
    const rebalancedMeals = rebalanceDietAcrossMeals({
      meals: currentMeals,
      modifiedMealKey: typedMealKey,
      modifiedOptIdx: optIdx,
      dailyCalorieTarget: dietPlan.measures.calories,
      resolveRecipe,
      selectedOptionIndices: { ...activeOption, [typedMealKey]: optIdx },
      lockModifiedMeal: true,
    });

    setMeals(rebalancedMeals);
    saveDiet(undefined, rebalancedMeals);
    toast({ title: "Item removido", description: "A opção foi recalculada." });
    setDeleteTarget(null);
  };

  /** Get available items for adding to a meal option */
  const getAddCandidates = (mealKey: string, optIdx: number): Recipe[] => {
    const block = enrichedRecipes[mealKey as MealKey];
    if (!block) return [];

    const options = dietPlan.meals[mealKey as MealKey] || [[]];
    const selectedIds = options[optIdx] || [];
    const selectedBaseIds = selectedIds.map((sid) => parseScaledRecipeId(sid).baseId);

    return block.items.filter((item) => {
      if (selectedBaseIds.includes(item.id)) return false;
      if (item.cal <= 0) return false;
      return true;
    });
  };

  /** Handle adding an item to a meal option and recalculating */
  const handleAddItem = (mealKey: string, optIdx: number, newRecipe: Recipe) => {
    const typedMealKey = mealKey as MealKey;
    const currentMeals = { ...dietPlan.meals };
    const options = [...currentMeals[typedMealKey].map((o) => [...o])];
    const oldOption = [...(options[optIdx] || [])];

    // Add the new recipe at scale 1
    const option = [...oldOption, newRecipe.id];

    const fallbackEntries = oldOption
      .map((id) => {
        const recipe = resolveRecipe(id);
        return recipe ? { id, recipe } : null;
      })
      .filter((e): e is { id: string; recipe: Recipe } => Boolean(e));
    const fallbackTarget = getMealTotalsFromEntries(fallbackEntries).cal;
    const targetCalories = budgets[typedMealKey] ?? fallbackTarget;

    // Build candidate preference pool (same strategy as handleDeleteItem) so the
    // closer can expand the flexible items to fill deficits.
    const preferenceIds = Array.from(
      new Set(
        (currentMeals[typedMealKey] || [])
          .flat()
          .map((id) => parseScaledRecipeId(id.replace(/:free$/, "")).baseId),
      ),
    );

    // Use banded closure so the meal prefers states within [99%, 105%] of its
    // budget instead of merely the mathematically closest one. This ensures
    // flexible items (e.g. pão + frango) are scaled up to absorb a newly added
    // low-flexibility item (e.g. leite desnatado) rather than letting the meal
    // sit well below budget.
    options[optIdx] = closeMealOptionToBudget({
      optionIds: option,
      targetCalories,
      resolveRecipe,
      availablePreferences: preferenceIds,
      maxAdditions: 0,
      maxOverRatio: 1.05,
      acceptRatio: 0.99,
    });

    currentMeals[typedMealKey] = options;

    // Lock the modified meal so the daily solver doesn't revert the local
    // closure to satisfy the global target. Residual kcal is absorbed by the
    // other meals, matching handleDeleteItem's behavior.
    const rebalancedMeals = rebalanceDietAcrossMeals({
      meals: currentMeals,
      modifiedMealKey: typedMealKey,
      modifiedOptIdx: optIdx,
      dailyCalorieTarget: dietPlan.measures.calories,
      resolveRecipe,
      selectedOptionIndices: { ...activeOption, [typedMealKey]: optIdx },
      lockModifiedMeal: true,
    });

    setMeals(rebalancedMeals);
    saveDiet(undefined, rebalancedMeals);
    toast({ title: "Item adicionado", description: "A opção foi recalculada." });
    setAddItemTarget(null);
  };

  const handleSubstitute = (mealKey: string, oldRecipeId: string, newScaledId: string, optIdx: number) => {
    const typedMealKey = mealKey as MealKey;
    const currentMeals = { ...dietPlan.meals };
    const options = [...currentMeals[typedMealKey].map((o) => [...o])];
    const option = options[optIdx] || [];
    const idx = option.indexOf(oldRecipeId);

    if (idx === -1) {
      setSwappingMeal(null);
      setSwappingRecipeId(null);
      return;
    }

    // Replace the item with the substituted one (already pre-scaled in preview).
    option[idx] = newScaledId;

    // Determine the meal budget. The substitute is meant to be calorie-neutral,
    // but discrete portion floors (whole eggs, fruits, bread halves, 30g cheese
    // minimum) frequently push the actual scaled calories above the preview.
    // We re-close the meal to the budget so the swap doesn't inflate kcal.
    const fallbackEntries = option
      .map((id) => {
        const recipe = resolveRecipe(id);
        return recipe ? { id, recipe } : null;
      })
      .filter((e): e is { id: string; recipe: Recipe } => Boolean(e));
    const fallbackTarget = getMealTotalsFromEntries(fallbackEntries).cal;
    const targetCalories = budgets[typedMealKey] ?? fallbackTarget;

    // Preference pool from items the user already accepted in this meal.
    const preferenceIds = Array.from(
      new Set(
        (currentMeals[typedMealKey] || [])
          .flat()
          .map((id) => parseScaledRecipeId(id.replace(/:free$/, "")).baseId),
      ),
    );

    // maxAdditions: 0 — the user only requested a swap; do not surprise them
    // with a brand-new item appearing in the option. We only re-solve the
    // existing items' scales to match the meal budget.
    options[optIdx] = closeMealOptionToBudget({
      optionIds: option,
      targetCalories,
      resolveRecipe,
      availablePreferences: preferenceIds,
      maxAdditions: 0,
      maxOverRatio: 1.05,
      acceptRatio: 0.99,
    });

    currentMeals[typedMealKey] = options;

    // Lock the modified meal so the daily solver pushes any residual kcal to
    // the OTHER meals — same pattern as handleAddItem / handleDeleteItem. This
    // guarantees the daily total stays on target after a swap.
    const rebalancedMeals = rebalanceDietAcrossMeals({
      meals: currentMeals,
      modifiedMealKey: typedMealKey,
      modifiedOptIdx: optIdx,
      dailyCalorieTarget: dietPlan.measures.calories,
      resolveRecipe,
      selectedOptionIndices: { ...activeOption, [typedMealKey]: optIdx },
      lockModifiedMeal: true,
    });

    setMeals(rebalancedMeals);
    saveDiet(undefined, rebalancedMeals);
    toast({ title: "Substituição realizada", description: "Sua dieta foi atualizada com sucesso." });
    setSwappingMeal(null);
    setSwappingRecipeId(null);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground text-center lg:text-left">Minha Dieta</h1>
          <p className="text-muted-foreground text-sm text-center lg:text-left">
            Criada em {new Date(dietPlan.createdAt).toLocaleDateString("pt-BR")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-center lg:justify-end">
          <Button
            onClick={exportPDF}
            size="sm"
            disabled={exporting}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            <span>Baixe sua dieta aqui</span>
          </Button>
          <Button
            onClick={() => setShowHistory(!showHistory)}
            size="sm"
            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
          >
            <History className="w-4 h-4" />
            <span>Histórico</span>
          </Button>
          <Button
            onClick={() => navigate("/diet-builder")}
            size="sm"
            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
          >
            <UtensilsCrossed className="w-4 h-4" />
            <span>Editar</span>
          </Button>
        </div>
      </div>

      {/* Diet History Panel */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <DietHistory
              diets={savedDiets}
              currentDietId={currentDietId}
              onExportPdf={async (diet) => {
                try {
                  await exportPDFForPlan({
                    measures: diet.measures,
                    meals: diet.meals,
                    createdAt: diet.created_at,
                    name: diet.name,
                  });
                } catch (e) {
                  console.error("PDF export error:", e);
                  toast({ title: "Erro ao exportar PDF", variant: "destructive" });
                }
              }}
              onDelete={deleteDiet}
              onSetCurrent={setCurrentDiet}
              loading={historyLoading}
              resolveRecipe={resolveRecipe}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Card */}
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">Meta diária (combinação selecionada)</p>
            <p className="text-xl font-bold text-foreground">
              {Math.round(globalTotals.cal)}{" "}
              <span className="text-sm font-normal text-muted-foreground">/ {targetCalories} kcal</span>
            </p>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${caloriePercentRaw > 105 ? "text-destructive" : "text-primary"}`}>
              {caloriePercentRaw}%
            </p>
          </div>
        </div>

        <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full gradient-primary"
            initial={{ width: 0 }}
            animate={{ width: `${caloriePercent}%` }}
            transition={{ duration: 0.6 }}
          />
        </div>

        {/* Macro targets comparison */}
        {correctedMacroTargets ? (
          <div className="space-y-2">
            <MacroProgressRow
              label="Proteínas"
              icon={Beef}
              actual={Math.round(globalTotals.protein)}
              target={correctedMacroTargets.protein}
              unit="g"
              color="text-primary"
            />
            <MacroProgressRow
              label="Carboidratos"
              icon={Wheat}
              actual={Math.round(globalTotals.carbs)}
              target={correctedMacroTargets.carbs}
              unit="g"
              color="text-accent"
            />
            <MacroProgressRow
              label="Gorduras"
              icon={Droplets}
              actual={Math.round(globalTotals.fat)}
              target={correctedMacroTargets.fat}
              unit="g"
              color="text-warning"
            />
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            <MacroCard
              icon={Flame}
              label="Calorias"
              value={Math.round(globalTotals.cal)}
              unit="kcal"
              color="text-primary"
            />
            <MacroCard
              icon={Beef}
              label="Proteínas"
              value={Math.round(globalTotals.protein)}
              unit="g"
              color="text-primary"
            />
            <MacroCard
              icon={Wheat}
              label="Carbos"
              value={Math.round(globalTotals.carbs)}
              unit="g"
              color="text-accent"
            />
            <MacroCard
              icon={Droplets}
              label="Gorduras"
              value={Math.round(globalTotals.fat)}
              unit="g"
              color="text-warning"
            />
          </div>
        )}

        {dietPlan.measures.mealTimes && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t border-border flex-wrap">
            <Calendar className="w-3.5 h-3.5" />
            <span>Horários:</span>
            {["Café", "L. Manhã", "Almoço", "L. Tarde", "Jantar"].map((name, i) => (
              <span key={name} className="bg-muted px-2 py-0.5 rounded text-foreground font-medium">
                {name} {dietPlan.measures.mealTimes[i] || "--:--"}
              </span>
            ))}
          </div>
        )}

        {/* Global option selector */}
        {(() => {
          const maxOptions = Math.max(
            ...activeMealKeys.map((key) => {
              const options = dietPlan.meals[key as MealKey] || [[]];
              return options.filter((opt) => opt.length > 0).length;
            }),
          );
          if (maxOptions <= 1) return null;

          // Detect global selection using the same fallback mapping used on click
          // (i-th valid option per meal, or last valid when meal has fewer options)
          const matchedGlobal = Array.from({ length: maxOptions }, (_, globalIdx) => globalIdx).find((globalIdx) => {
            return activeMealKeys.every((key) => {
              const validIndices = getValidOptionIndices(key);
              const mappedIdx = validIndices[Math.min(globalIdx, validIndices.length - 1)] ?? 0;
              return getActiveOptionIndex(key) === mappedIdx;
            });
          });

          const currentGlobal: number | "custom" = typeof matchedGlobal === "number" ? matchedGlobal : "custom";

          return (
            <div
              ref={globalOptionsDrag.ref}
              onPointerDown={globalOptionsDrag.onPointerDown}
              onPointerMove={globalOptionsDrag.onPointerMove}
              onPointerUp={globalOptionsDrag.onPointerUp}
              onPointerCancel={globalOptionsDrag.onPointerCancel}
              onPointerLeave={globalOptionsDrag.onPointerUp}
              onClickCapture={globalOptionsDrag.onClickCapture}
              className="flex items-center gap-2 overflow-x-auto pt-2 border-t border-border scrollbar-hide touch-pan-x select-none"
            >
              <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">Opção:</span>
              {Array.from({ length: maxOptions }, (_, i) => {
                return (
                  <button
                    key={i}
                    onClick={() => {
                      const newActive: Record<string, number> = {};
                      for (const key of activeMealKeys) {
                        const validIndices = getValidOptionIndices(key);
                        // Pick the i-th valid index, or the last valid one
                        newActive[key] = validIndices[Math.min(i, validIndices.length - 1)];
                      }
                      setActiveOption(newActive);
                    }}
                    className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                      currentGlobal === i
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                  >
                    Opção {i + 1}
                  </button>
                );
              })}
              <button
                className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  currentGlobal === "custom"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                disabled
              >
                Customizada
              </button>
            </div>
          );
        })()}
      </div>

      {/* Water Goal Card */}
      {(() => {
        const weight = dietPlan.measures.weight;
        const goal = dietPlan.measures.goal;
        const waterMultiplier = goal === "gain" ? 0.045 : goal === "lose" ? 0.04 : 0.035;
        const waterLiters = weight ? Math.min(4, Math.max(2, weight * waterMultiplier)).toFixed(1) : "2.0";
        const waterMl = Math.round(Number(waterLiters) * 1000);
        const glasses = Math.ceil(waterMl / 250);

        return (
          <div className="bg-card rounded-2xl p-5 shadow-card border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Droplets className="w-5 h-5 text-blue-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Meta diária de água</p>
                <p className="text-xl font-bold text-foreground">
                  {waterLiters}L{" "}
                  <span className="text-sm font-normal text-muted-foreground">({glasses} copos de 250ml)</span>
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 bg-muted/50 rounded-xl px-3 py-2">
              💡 Calculado com base no seu peso ({weight}kg) e objetivo (
              {goal === "gain" ? "ganho de massa" : goal === "lose" ? "emagrecimento" : "manutenção"}). Acompanhe seu
              consumo no <span className="font-medium text-foreground">Diário Alimentar</span>.
            </p>
          </div>
        );
      })()}

      {/* Meal Blocks */}
      {activeMealKeys.map((key) => {
        const config = mealConfig[key];
        const options = dietPlan.meals[key as MealKey] || [[]];
        const validOptionIndices = options.map((opt, idx) => (opt.length > 0 ? idx : -1)).filter((idx) => idx >= 0);
        const hasMultipleOptions = validOptionIndices.length > 1;
        const currentOptIdx = getActiveOptionIndex(key);
        const mealRecipeEntries = getOptionRecipeEntries(key, currentOptIdx);
        const mealRecipes = mealRecipeEntries.map(({ recipe }) => recipe);
        const totals = getMealTotalsFromEntries(mealRecipeEntries);
        const isExpanded = expandedMeal === key;
        const budget = budgets[key as MealKey];

        return (
          <div key={key} className="bg-card rounded-2xl shadow-card border border-border overflow-hidden">
            <button onClick={() => setExpandedMeal(isExpanded ? null : key)} className="w-full text-left p-4 md:p-5">
              {/* Mobile layout: stacked rows */}
              <div className="md:hidden space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="text-2xl leading-none">{config.emoji}</span>
                    <h2 className="font-semibold text-foreground text-base">{config.label}</h2>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {hasMultipleOptions && (
                    <span className="text-xs text-primary bg-primary/10 px-2.5 py-0.5 rounded-full font-medium">
                      {validOptionIndices.length} opções
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground bg-muted px-2.5 py-0.5 rounded-full">
                    {mealRecipes.length} {mealRecipes.length === 1 ? "item" : "itens"}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium bg-secondary text-secondary-foreground px-2 py-0.5 rounded-lg">
                    🔥 {Math.round(totals.cal)} kcal
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-lg">
                    P {(Math.round(totals.protein * 10) / 10).toString().replace(/\.0$/, "")}g
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-lg">
                    C {Math.round(totals.carbs)}g
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-lg">
                    G {Math.round(totals.fat)}g
                  </span>
                  <span className="text-xs text-primary/70 bg-primary/5 px-2 py-0.5 rounded-lg">{budget} kcal</span>
                </div>
              </div>

              {/* Desktop/Tablet layout: compact rows with chips */}
              <div className="hidden md:flex md:flex-col md:gap-3">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{config.emoji}</span>
                    <h2 className="font-semibold text-foreground text-base">{config.label}</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasMultipleOptions && (
                      <span className="text-xs text-primary bg-primary/10 px-2.5 py-1 rounded-full font-medium">
                        {validOptionIndices.length} opções
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                      {mealRecipes.length} {mealRecipes.length === 1 ? "item" : "itens"}
                    </span>
                    <span className="text-xs font-semibold text-primary-foreground bg-primary px-4 py-1.5 rounded-md shadow-sm hover:bg-primary/90 transition-colors cursor-pointer">
                      {isExpanded ? "Fechar" : "Ver Refeição"}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium bg-secondary text-secondary-foreground px-2.5 py-1 rounded-lg">
                    🔥 {Math.round(totals.cal)} kcal
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-lg">
                    P {(Math.round(totals.protein * 10) / 10).toString().replace(/\.0$/, "")}g
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-lg">
                    C {Math.round(totals.carbs)}g
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-lg">
                    G {Math.round(totals.fat)}g
                  </span>
                  <span className="text-xs text-primary/70 bg-primary/5 px-2.5 py-1 rounded-lg">{budget} kcal</span>
                </div>
              </div>
            </button>

            {!isExpanded && (
              <div className="px-5 pb-4 flex flex-wrap gap-2">
                {mealRecipeEntries.map(({ id, recipe }) => (
                  <span
                    key={id}
                    className="bg-secondary text-secondary-foreground px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5"
                  >
                    <RecipeEmoji emoji={recipe.emoji} />
                    {recipe.label}
                  </span>
                ))}
              </div>
            )}

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5 space-y-3">
                    {/* Option tabs */}
                    {hasMultipleOptions && (
                      <DragScrollContainer className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide touch-pan-x select-none">
                        {validOptionIndices.map((optionIdx, i) => {
                          const optTotals = getMealTotalsFromEntries(getOptionEntries(key, optionIdx));
                          return (
                            <button
                              key={optionIdx}
                              onClick={() => setActiveOption((prev) => ({ ...prev, [key]: optionIdx }))}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                                optionIdx === currentOptIdx
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground hover:bg-muted/80"
                              }`}
                            >
                              Opção {i + 1}
                              <span
                                className={`text-[10px] ${optionIdx === currentOptIdx ? "text-primary-foreground/70" : "text-muted-foreground"}`}
                              >
                                ({Math.round(optTotals.cal)} kcal)
                              </span>
                            </button>
                          );
                        })}
                      </DragScrollContainer>
                    )}

                    {(() => {
                      // Count items the user CAN remove (not salad, not free beverage).
                      // We only block deletion when removing this item would
                      // leave ZERO real items in the meal — never above that.
                      const deletableCount = mealRecipeEntries.filter(
                        ({ id, recipe }) => recipe.category !== "salad" && !id.endsWith(":free"),
                      ).length;
                      return mealRecipeEntries.map(({ id: recipeId, recipe }) => {
                        const showIngredients = expandedRecipe === recipeId;
                        const isSwapping = swappingMeal === key && swappingRecipeId === recipeId;
                        const substitutions = getSubstitutions(key, recipeId, currentOptIdx);
                        const isSalad = recipe.category === "salad";
                        const isFree = recipeId.endsWith(":free");
                        // Only block when this is the final non-salad/non-free item AND there are no salads/free items left;
                        // otherwise the meal would become entirely empty.
                        const totalEntries = mealRecipeEntries.length;
                        const isLastDeletable = !isSalad && !isFree && deletableCount <= 1 && totalEntries <= 1;
                        const totalCarbs = isSalad ? 0 : recipe.ingredients.reduce((s, i) => s + i.carbs, 0);
                        const totalFat = isSalad ? 0 : recipe.ingredients.reduce((s, i) => s + i.fat, 0);

                        return (
                          <div key={recipeId} className="border border-border rounded-xl overflow-hidden">
                            <div className="flex items-center justify-between p-3 bg-background">
                              <div className="flex items-center gap-2">
                                <RecipeEmoji emoji={recipe.emoji} className="text-lg" />
                                <div>
                                  <p className="text-sm font-semibold text-foreground">
                                    {recipe.label}
                                    {!isSalad &&
                                      (() => {
                                        const totalGrams = recipe.ingredients.reduce((sum, ing) => {
                                          const match = ing.amount.match(/(\d+)\s*g/);
                                          return sum + (match ? parseInt(match[1], 10) : 0);
                                        }, 0);
                                        return totalGrams > 0 ? (
                                          <span className="text-muted-foreground font-normal text-xs ml-1.5">
                                            ({totalGrams}g)
                                          </span>
                                        ) : null;
                                      })()}
                                  </p>
                                  {isSalad ? (
                                    <span className="text-xs text-primary font-medium italic">🥗 À vontade</span>
                                  ) : (
                                    <div className="flex gap-2 text-xs text-muted-foreground">
                                      <span className="text-primary font-medium">{Math.round(recipe.cal)} kcal</span>
                                      <span>{Math.round(recipe.protein)}g P</span>
                                      <span>{Math.round(totalCarbs)}g C</span>
                                      <span>{Math.round(totalFat)}g G</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setExpandedRecipe(showIngredients ? null : recipeId)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary text-secondary-foreground text-xs font-medium shadow-sm hover:bg-secondary/80 transition-colors"
                                >
                                  <Info className="w-3.5 h-3.5" />
                                  {showIngredients ? "Fechar" : "Ver Ingredientes"}
                                </button>
                                {canSubstitute ? (
                                  substitutions.length > 0 && (
                                    <button
                                      onClick={() => {
                                        if (isSwapping) {
                                          setSwappingMeal(null);
                                          setSwappingRecipeId(null);
                                        } else {
                                          setSwappingMeal(key);
                                          setSwappingRecipeId(recipeId);
                                        }
                                      }}
                                      className={`text-xs font-semibold px-3 py-1.5 rounded-md shadow-sm transition-colors cursor-pointer ${isSwapping ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}
                                      title="Substituir alimento"
                                    >
                                      Trocar Refeição
                                    </button>
                                  )
                                ) : (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={() => navigate("/plans")}
                                          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground/50 transition-colors"
                                          title="Recurso exclusivo para planos Plus e Max"
                                        >
                                          <ArrowRightLeft className="w-4 h-4" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs">
                                        Substituições disponíveis nos planos PRO FIT PLUS e MAX
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {/* Delete item button */}
                                {canSubstitute ? (
                                  isLastDeletable ? (
                                    <TooltipProvider delayDuration={200}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            disabled
                                            className="p-1.5 rounded-lg text-muted-foreground/30 cursor-not-allowed"
                                            title="Não é possível remover o último item"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent>A opção deve ter pelo menos 1 item</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setDeleteTarget({
                                          mealKey: key,
                                          recipeId,
                                          optIdx: currentOptIdx,
                                          label: recipe.label,
                                        });
                                      }}
                                      className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                      title="Remover item"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )
                                ) : (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={() => navigate("/plans")}
                                          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground/50 transition-colors"
                                          title="Recurso exclusivo para planos Plus e Max"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs">
                                        Remoção de itens disponível nos planos PRO FIT PLUS e MAX
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </div>

                            <AnimatePresence>
                              {showIngredients && (
                                <motion.div
                                  initial={{ height: 0 }}
                                  animate={{ height: "auto" }}
                                  exit={{ height: 0 }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-3 pb-3 space-y-1.5">
                                    {recipe.category === "salad" && (
                                      <p className="text-xs text-muted-foreground italic bg-muted/50 rounded-lg px-3 py-2">
                                        🥗 Saladas à vontade, atenção à quantidade de molhos e azeite.
                                      </p>
                                    )}
                                    {recipe.ingredients.map((ing, i) => (
                                      <div
                                        key={i}
                                        className="flex flex-col md:flex-row md:items-center md:justify-between text-xs py-1.5 border-t border-border/50 first:border-0 gap-1"
                                      >
                                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                          <span className="text-foreground font-medium break-words">{ing.name}</span>
                                          {!isSalad && (
                                            <span className="text-muted-foreground shrink-0">({ing.amount})</span>
                                          )}
                                          {!isSalad && ing.source && (
                                            <TooltipProvider delayDuration={200}>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <span
                                                    className={[
                                                      "text-[9px] font-bold px-1 py-0.5 rounded cursor-help shrink-0",
                                                      ing.source === "TACO"
                                                        ? "bg-green-500/15 text-green-700 dark:text-green-400"
                                                        : ing.source === "TBCA"
                                                          ? "bg-blue-500/15 text-blue-700 dark:text-blue-400"
                                                          : ing.source === "USDA"
                                                            ? "bg-purple-500/15 text-purple-700 dark:text-purple-400"
                                                            : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
                                                    ].join(" ")}
                                                  >
                                                    {ing.source}
                                                  </span>
                                                </TooltipTrigger>
                                                <TooltipContent side="top" className="max-w-[260px] text-xs">
                                                  {ing.source === "TACO" &&
                                                    "TACO — Tabela Brasileira de Composição de Alimentos (UNICAMP). Referência oficial para alimentos brasileiros."}
                                                  {ing.source === "TBCA" &&
                                                    "TBCA — Tabela Brasileira de Composição de Alimentos (USP). Base complementar com dados atualizados."}
                                                  {ing.source === "USDA" &&
                                                    "USDA — Departamento de Agricultura dos EUA. Referência internacional com ampla cobertura de alimentos."}
                                                  {ing.source === "ESTIMADO" &&
                                                    "Valor estimado com base em rótulos e tabelas nutricionais genéricas. Sem correspondência direta em bases oficiais."}
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>
                                          )}
                                        </div>
                                        {!isSalad && (
                                          <div className="flex gap-2 text-muted-foreground whitespace-nowrap md:ml-auto pl-0 md:pl-2">
                                            <span>{ing.cal} kcal</span>
                                            <span>{ing.protein}g P</span>
                                            <span>{ing.carbs}g C</span>
                                            <span>{ing.fat}g G</span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>

                            <AnimatePresence>
                              {isSwapping && (
                                <motion.div
                                  initial={{ height: 0 }}
                                  animate={{ height: "auto" }}
                                  exit={{ height: 0 }}
                                  className="overflow-hidden"
                                >
                                  <div className="p-3 bg-primary/5 border-t border-primary/20">
                                    <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                                      <RefreshCw className="w-3.5 h-3.5 text-primary" />
                                      Substituir por:
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                      {substitutions.map((sub) => {
                                        const subCarbs = sub.ingredients.reduce((s, i) => s + i.carbs, 0);
                                        const subFat = sub.ingredients.reduce((s, i) => s + i.fat, 0);
                                        return (
                                          <button
                                            key={sub.id}
                                            onClick={() => handleSubstitute(key, recipeId, sub.id, currentOptIdx)}
                                            className="flex items-center gap-2 p-2 rounded-lg border border-border bg-card hover:border-primary/50 transition-colors text-left"
                                          >
                                            <RecipeEmoji emoji={sub.emoji} className="text-lg" />
                                            <div className="min-w-0">
                                              <p className="text-xs font-medium text-foreground truncate">
                                                {sub.label}
                                              </p>
                                              <p className="text-[10px] text-muted-foreground">
                                                {sub.cal} kcal • {sub.protein}g P • {Math.round(subCarbs)}g C •{" "}
                                                {Math.round(subFat)}g G
                                              </p>
                                            </div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      });
                    })()}

                    {/* Add item button */}
                    {canSubstitute ? (
                      budget && totals.cal >= budget * 1.1 ? (
                        <div className="w-full flex items-center gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-muted-foreground">
                          <Info className="w-4 h-4 text-amber-500 shrink-0" />
                          <span>
                            Você atingiu o limite de calorias para esta refeição. Para adicionar outro alimento, exclua
                            um alimento primeiro.
                          </span>
                        </div>
                      ) : (
                        <button
                          onClick={() => setAddItemTarget({ mealKey: key, optIdx: currentOptIdx })}
                          className="w-full flex items-center justify-center gap-2 p-2.5 rounded-xl border border-dashed border-primary/30 text-primary text-xs font-medium hover:bg-primary/5 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Adicionar alimento
                        </button>
                      )
                    ) : (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => navigate("/plans")}
                              className="w-full flex items-center justify-center gap-2 p-2.5 rounded-xl border border-dashed border-muted-foreground/20 text-muted-foreground/50 text-xs font-medium hover:bg-muted/50 transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                              Adicionar alimento
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Adição de itens disponível nos planos PRO FIT PLUS e MAX
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 text-xs font-medium">
                      <span className="text-foreground">
                        Total {config.label}
                        {hasMultipleOptions ? ` (Opção ${currentOptIdx + 1})` : ""}
                      </span>
                      <div className="flex gap-3">
                        <span className="text-primary">{Math.round(totals.cal)} kcal</span>
                        <span className="text-foreground">{Math.round(totals.protein)}g P</span>
                        <span className="text-foreground">{Math.round(totals.carbs)}g C</span>
                        <span className="text-foreground">{Math.round(totals.fat)}g G</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {/* Tips */}
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border space-y-3">
        <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
          <Info className="w-4 h-4 text-primary" />
          Dicas
        </h3>
        <div className="space-y-2 text-xs text-muted-foreground">
          <p>💧 Beba pelo menos 2L de água por dia</p>
          <p>⏰ Respeite os horários das refeições para melhor metabolismo</p>
          <p>
            🔄 Use o botão <ArrowRightLeft className="w-3 h-3 inline" /> para substituir alimentos mantendo o equilíbrio
          </p>
          <p>📊 As substituições recalculam automaticamente os macros do dia</p>
          <p>📄 Exporte sua dieta em PDF para imprimir ou compartilhar</p>
          <p>⭐ Crie múltiplas opções por refeição para ter variedade no dia a dia</p>
        </div>
      </div>

      <Button onClick={() => navigate("/diet-builder")} variant="outline" className="w-full">
        Remontar dieta do zero
      </Button>

      {/* Delete item confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover item</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{deleteTarget?.label}</strong> desta opção? Os demais itens serão
              recalculados automaticamente para manter o valor calórico da refeição.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteTarget && handleDeleteItem(deleteTarget.mealKey, deleteTarget.recipeId, deleteTarget.optIdx)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add item dialog */}
      <Dialog open={!!addItemTarget} onOpenChange={(open) => !open && setAddItemTarget(null)}>
        <DialogContent className="max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Adicionar alimento
            </DialogTitle>
            <DialogDescription>
              Selecione um alimento para adicionar à opção. Os itens serão recalculados automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto space-y-2 pr-1 flex-1">
            {addItemTarget &&
              (() => {
                const candidates = getAddCandidates(addItemTarget.mealKey, addItemTarget.optIdx);
                if (candidates.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      Nenhum item disponível para adicionar.
                    </p>
                  );
                }

                const mealKey = addItemTarget.mealKey as MealKey;
                const mealBudget = budgets[mealKey] || 0;
                const currentEntries = getOptionEntries(mealKey, addItemTarget.optIdx);
                const currentCal = getMealTotalsFromEntries(currentEntries).cal;
                const ceiling = mealBudget * 1.1;
                const currentOptionIds = ((dietPlan.meals[mealKey] || [])[addItemTarget.optIdx] || []) as string[];
                const preferenceIds = Array.from(
                  new Set(
                    (dietPlan.meals[mealKey] || [])
                      .flat()
                      .map((id) => parseScaledRecipeId(String(id).replace(/:free$/, "")).baseId),
                  ),
                ) as string[];

                return candidates.map((candidate) => {
                  const cCarbs = candidate.ingredients.reduce((s, i) => s + i.carbs, 0);
                  const cFat = candidate.ingredients.reduce((s, i) => s + i.fat, 0);
                  const isSalad = candidate.category === "salad";
                  // Simulate the rebalance: if closeMealOptionToBudget can absorb
                  // the new item by scaling existing items down, we don't warn.
                  // Only warn if the rebalanced result still exceeds the ceiling.
                  let wouldExceed = false;
                  if (!isSalad && mealBudget > 0) {
                    try {
                      const simulated = closeMealOptionToBudget({
                        optionIds: [...currentOptionIds, candidate.id],
                        targetCalories: mealBudget,
                        resolveRecipe,
                        availablePreferences: preferenceIds,
                        maxAdditions: 0,
                        maxOverRatio: 1.05,
                        acceptRatio: 0.99,
                      });
                      const simulatedCal = simulated.reduce((sum, id) => {
                        const r = resolveRecipe(id);
                        if (!r || r.category === "salad") return sum;
                        return sum + r.cal;
                      }, 0);
                      wouldExceed = simulatedCal > ceiling;
                    } catch {
                      // Fallback: naive check (preserves previous behavior).
                      wouldExceed = candidate.cal > Math.max(0, ceiling - currentCal);
                    }
                  }

                  return (
                    <button
                      key={candidate.id}
                      onClick={() => {
                        if (wouldExceed) {
                          setPendingExceedItem({
                            mealKey: addItemTarget.mealKey,
                            optIdx: addItemTarget.optIdx,
                            candidate,
                          });
                          return;
                        }
                        handleAddItem(addItemTarget.mealKey, addItemTarget.optIdx, candidate);
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left",
                        wouldExceed
                          ? "border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 hover:border-amber-400"
                          : "border-border bg-card hover:border-primary/50 hover:bg-primary/5",
                      )}
                    >
                      <RecipeEmoji emoji={candidate.emoji} className="text-xl" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{candidate.label}</p>
                        {isSalad ? (
                          <p className="text-xs text-primary italic">🥗 À vontade</p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            {candidate.cal} kcal • {candidate.protein}g P • {Math.round(cCarbs)}g C • {Math.round(cFat)}
                            g G
                          </p>
                        )}
                        {wouldExceed && (
                          <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            Ultrapassa o limite de calorias da refeição
                          </p>
                        )}
                      </div>
                      <Plus className={cn("w-4 h-4 shrink-0", wouldExceed ? "text-amber-500" : "text-primary")} />
                    </button>
                  );
                });
              })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Exceed calorie confirmation dialog */}
      <AlertDialog open={!!pendingExceedItem} onOpenChange={(open) => !open && setPendingExceedItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-amber-500" />
              Limite de calorias
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingExceedItem && (
                <>
                  O item <strong>{pendingExceedItem.candidate.label}</strong> ({pendingExceedItem.candidate.cal} kcal)
                  vai ultrapassar o limite de calorias desta refeição. Deseja adicionar mesmo assim?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="gradient-primary text-primary-foreground"
              onClick={() => {
                if (pendingExceedItem) {
                  handleAddItem(pendingExceedItem.mealKey, pendingExceedItem.optIdx, pendingExceedItem.candidate);
                  setPendingExceedItem(null);
                }
              }}
            >
              Adicionar mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

const goalLabel = (goal: string) => {
  switch (goal) {
    case "lose":
      return "Emagrecimento";
    case "gain":
      return "Ganho de Massa";
    case "maintain":
      return "Manutenção";
    default:
      return "";
  }
};

const activityLabel = (level: string) => {
  switch (level) {
    case "sedentary": return "Sedentário";
    case "light": return "Leve";
    case "moderate": return "Moderado";
    case "active": return "Ativo";
    case "very_active": return "Muito ativo";
    default: return "";
  }
};

const formatDietBodyData = (measures: any): string => {
  if (!measures) return "";
  const parts: string[] = [];
  if (measures.calories) parts.push(`${measures.calories} kcal/dia`);
  if (measures.weight) parts.push(`${measures.weight}kg`);
  if (measures.height) parts.push(`${measures.height}cm`);
  if (measures.age) parts.push(`${measures.age} anos`);
  const a = activityLabel(measures.activityLevel);
  if (a) parts.push(a);
  return parts.join(" · ");
};

const planDisplayName = (plan: string) => {
  switch (plan) {
    case "basic":
      return "PRO FIT";
    case "plus":
      return "PRO FIT PLUS";
    case "complete":
      return "PRO FIT MAX";
    default:
      return plan.toUpperCase();
  }
};

const planBadgeClass = (plan: string) => {
  switch (plan) {
    case "basic":
      return "bg-blue-500/15 text-blue-600 dark:text-blue-400";
    case "plus":
      return "bg-purple-500/15 text-purple-600 dark:text-purple-400";
    case "complete":
      return "bg-amber-500/15 text-amber-600 dark:text-amber-400";
    default:
      return "bg-muted text-muted-foreground";
  }
};

// Diet History Component
const DietHistory = ({
  diets,
  currentDietId,
  onExportPdf,
  onDelete,
  onSetCurrent,
  loading,
  resolveRecipe,
}: {
  diets: SavedDiet[];
  currentDietId: string | null;
  onExportPdf: (d: SavedDiet) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSetCurrent: (id: string) => Promise<void>;
  loading: boolean;
  resolveRecipe: (id: string) => Recipe | undefined;
}) => {
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const { toast } = useToast();

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border text-center">
        <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
      </div>
    );
  }

  if (diets.length === 0) {
    return (
      <div className="bg-card rounded-2xl p-5 shadow-card border border-border text-center">
        <History className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Nenhuma dieta salva ainda</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl p-5 shadow-card border border-border space-y-3">
      <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
        <History className="w-4 h-4 text-primary" />
        Histórico de Dietas ({diets.length})
      </h3>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {diets.map((diet) => {
          const isCurrent = diet.id === currentDietId;
          const bodyDataLine = formatDietBodyData(diet.measures);

          return (
            <div
              key={diet.id}
              className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${
                isCurrent ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {isCurrent && <Star className="w-4 h-4 text-primary flex-shrink-0" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground truncate">
                      {diet.name}
                      {(diet.measures as any)?.goal && ` - ${goalLabel((diet.measures as any).goal)}`}
                    </p>
                    {diet.plan && diet.plan !== "free" && (
                      <Badge
                        className={`text-[10px] px-1.5 py-0 leading-4 border-0 font-semibold ${planBadgeClass(diet.plan)}`}
                      >
                        {planDisplayName(diet.plan)}
                      </Badge>
                    )}
                    {isCurrent && <span className="text-xs text-primary font-medium">(Atual)</span>}
                  </div>
                  <p className="text-xs text-muted-foreground break-words">
                    {new Date(diet.created_at).toLocaleDateString("pt-BR")}
                    {bodyDataLine && ` • ${bodyDataLine}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {!isCurrent && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 px-2 text-xs border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    disabled={activatingId === diet.id}
                    onClick={async () => {
                      setActivatingId(diet.id);
                      try {
                        await onSetCurrent(diet.id);
                        toast({ title: "Dieta ativada", description: "Esta agora é a sua dieta atual." });
                      } catch (e) {
                        toast({ title: "Erro ao ativar dieta", variant: "destructive" });
                      } finally {
                        setActivatingId(null);
                      }
                    }}
                  >
                    {activatingId === diet.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    )}
                    <span>Usar esta dieta</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  className="h-8 px-2 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={exportingId === diet.id}
                  onClick={async () => {
                    setExportingId(diet.id);
                    try {
                      await onExportPdf(diet);
                    } finally {
                      setExportingId(null);
                    }
                  }}
                >
                  {exportingId === diet.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FileDown className="w-3.5 h-3.5" />
                  )}
                  <span>Baixar dieta</span>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Excluir dieta</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir "{diet.name}"? Essa ação não pode ser desfeita.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => {
                          onDelete(diet.id);
                          toast({ title: "Dieta removida" });
                        }}
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MacroCard = ({
  icon: Icon,
  label,
  value,
  unit,
  color,
}: {
  icon: any;
  label: string;
  value: number;
  unit: string;
  color: string;
}) => {
  const displayValue =
    unit === "kcal" ? Math.round(value).toString() : (Math.round(value * 10) / 10).toString().replace(/\.0$/, "");

  return (
    <div className="bg-muted/50 rounded-xl p-2.5 text-center">
      <Icon className={`w-3.5 h-3.5 mx-auto mb-0.5 ${color}`} />
      <p className="text-sm font-bold text-foreground">
        {displayValue}
        <span className="text-[10px] font-normal text-muted-foreground ml-0.5">{unit}</span>
      </p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
};

const MacroProgressRow = ({
  icon: Icon,
  label,
  actual,
  target,
  unit,
  color,
}: {
  icon: any;
  label: string;
  actual: number;
  target: number;
  unit: string;
  color: string;
}) => {
  const rawPct = target > 0 ? (actual / target) * 100 : 0;
  const displayPct = Math.max(Math.round(rawPct * 10) / 10, 0);
  const barPct = Math.min(Math.max(rawPct, 0), 100);
  const displayActual = (Math.round(actual * 10) / 10).toString().replace(/\.0$/, "");
  const displayTarget = (Math.round(target * 10) / 10).toString().replace(/\.0$/, "");

  return (
    <div className="flex items-center gap-3">
      <Icon className={`w-4 h-4 flex-shrink-0 ${color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-foreground">{label}</span>
          <span className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{displayActual}</span> / {displayTarget}
            {unit}
            <span className="ml-1 text-primary font-medium">({displayPct}%)</span>
          </span>
        </div>
        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full gradient-primary transition-all duration-500"
            style={{ width: `${barPct}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default MyDiet;
